import type { Theme } from '@mui/material/styles';

import {
  getStatusChipColor,
  getStatusChipColors,
  StatusChipToken,
} from 'app/utils/statusChipColors';

const STATUS_COLOR_ALIASES: Record<string, StatusChipToken> = {
  SUCCESS: 'SUCCESS',
  COMPLETED: 'SUCCESS',

  RUNNING: 'RUNNING',
  ACTIVE: 'RUNNING',
  AVAILABLE: 'SUCCESS',
  UNDER_NURTURING: 'RUNNING',
  RESTARTING: 'PENDING',

  FAILED: 'FAILED',
  FAILURE: 'FAILED',
  INACTIVE: 'FAILED',
  UPSTREAM_FAILED: 'FAILED',

  PENDING: 'PENDING',
  WARNING: 'WARNING',
  WARN: 'WARNING',
  ONBOARDING: 'PENDING',
  PAUSED: 'PENDING',
  INITIALIZED_NURTURING_AUTOMATION: 'PENDING',
  SHUTDOWN: 'PENDING',
  DEFERRED: 'PENDING',
  QUEUED: 'PENDING',
  SCHEDULED: 'PENDING',
  UP_FOR_RESCHEDULE: 'PENDING',
  UP_FOR_RETRY: 'PENDING',

  NOT_LAUNCHED: 'NO_STATUS',
  FUTURE: 'PENDING',
  NOT_RUN: 'PENDING',

  SKIPPED: 'SKIPPED',
  ARCHIVED: 'NO_STATUS',
  NOT_UNDER_NURTURING: 'NO_STATUS',
  REMOVED: 'NO_STATUS',

  NOT_AVAILABLE: 'NO_STATUS',
  NO_STATUS: 'NO_STATUS',

  IDENTIFIED: 'PENDING',
  UNIDENTIFIED: 'NO_STATUS',
  NEEDS_TO_BE_RESOLVED: 'WARNING',
  UNQUALIFIED: 'FAILED',
  AVAILABLE_FOR_NURTURING: 'SUCCESS',
  NOT_AVAILABLE_FOR_NURTURING: 'FAILED',
  ATTRIBUTION_CANDIDATE: 'PENDING',
  CONTACTED: 'SUCCESS',
  SOLD: 'SUCCESS',
  UNSUBSCRIBED: 'SKIPPED',
  DISABLED: 'FAILED',
  IDENTIFIED_DUPLICATE: 'WARNING',
  IDENTIFIED_POTENTIAL_DUPLICATE: 'WARNING',
  IDENTIFIED_DIFFERENT_LEAD_TYPE: 'WARNING',
  IDENTIFIED_POTENTIAL_DIFFERENT_LEAD_TYPE: 'WARNING',
  PICKED_FOR_NURTURING: 'SUCCESS',
  REJECTED_FOR_NURTURING: 'FAILED',
  OPPORTUNITY_ADDED: 'SUCCESS',
  ATTRIBUTED: 'SUCCESS',
  REVIVED_BEFORE_NURTURING: 'PENDING',
  IGNORED: 'SKIPPED',
};

export const normalizeStatusKey = (
  rawStatus: string | null | undefined,
): string => {
  const trimmed = String(rawStatus ?? '').trim();
  return trimmed ? trimmed.toUpperCase().replace(/\s+/g, '_') : 'NO_STATUS';
};

export const resolveStatusToken = (key: string): StatusChipToken => {
  if (STATUS_COLOR_ALIASES[key]) {
    return STATUS_COLOR_ALIASES[key];
  }

  if (key.includes('UNDER_NURTURING')) {
    return 'RUNNING';
  }

  if (
    key.includes('REJECTED') ||
    key.includes('UNQUALIFIED') ||
    key.includes('DISABLED')
  ) {
    return 'FAILED';
  }

  if (
    key.includes('PICKED_FOR') ||
    key.includes('OPPORTUNITY_ADDED') ||
    key.includes('CONTACTED') ||
    key.includes('SOLD') ||
    key.includes('ATTRIBUTED')
  ) {
    return 'SUCCESS';
  }

  if (key.includes('AVAILABLE') && !key.includes('NOT_AVAILABLE')) {
    return 'SUCCESS';
  }

  if (key.includes('IGNORED') || key.includes('UNSUBSCRIBED')) {
    return 'SKIPPED';
  }

  if (
    key.includes('NEEDS_TO_BE_RESOLVED') ||
    key.includes('DUPLICATE') ||
    key.includes('DIFFERENT_LEAD')
  ) {
    return 'WARNING';
  }

  if (
    key.includes('INITIALIZED') ||
    key.includes('REVIVED') ||
    key.includes('ATTRIBUTION')
  ) {
    return 'PENDING';
  }

  return 'NO_STATUS';
};

export const getStatusTokenFromRaw = (
  rawStatus: string | null | undefined,
): StatusChipToken => resolveStatusToken(normalizeStatusKey(rawStatus));

export const formatStatusLabel = (key: string): string =>
  key
    .toLowerCase()
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const getStatusChipBackgroundColor = (
  theme: Theme,
  rawStatus: string | null | undefined,
): string => {
  const token = getStatusTokenFromRaw(rawStatus);
  return getStatusChipColor(theme, token);
};

export const getStatusChipForegroundColor = (
  theme: Theme,
  rawStatus: string | null | undefined,
): string => {
  const token = getStatusTokenFromRaw(rawStatus);

  if (token === 'NO_STATUS') {
    return theme.palette.mode === 'dark'
      ? theme.palette.grey[200]
      : theme.palette.grey[700];
  }

  return theme.palette.common.white;
};

/** Text color for plain status labels (dropdowns, captions) using mapper tokens. */
export const getStatusLabelColor = (
  theme: Theme,
  rawStatus: string | null | undefined,
): string => {
  const token = getStatusTokenFromRaw(rawStatus);
  const colors = getStatusChipColors(theme);

  if (token === 'NO_STATUS') {
    return theme.palette.mode === 'dark'
      ? theme.palette.grey[400]
      : theme.palette.grey[600];
  }

  return colors[token];
};
